import { TooltipV2 } from '@cimplico/components';
import { ArrowTopRightOnSquareIcon, BanknotesIcon, CurrencyDollarIcon } from '@heroicons/react/20/solid';
import { Link, useSearchParams } from 'react-router-dom';
import Currency from '../../components/currency/Currency';
import { useFirmContext } from '../../providers/FirmProvider';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { Worksheet, WorksheetWithAccount } from '../../types/worksheet';
import ClosingAmountDiffWarning from '../trial-balance/ClosingAmountDiffWarning';
import WorksheetVariance from '../trial-balance/WorksheetVariance';
import Assigned from '../worksheet-v2/top-section/Assigned';

import StatusPreview from './StatusPreview';

type PreviewDetailsProps = {
    worksheet: Worksheet;
};

function PreviewDetails({ worksheet }: PreviewDetailsProps): JSX.Element {
    const [params, setParams] = useSearchParams();
    const workpaper = useWorkpaperContext();
    const { shortId } = useFirmContext();

    const hasWorkpaperAmount =
        worksheet.account?.workpaperDebitTY !== worksheet.account?.ledgerDebitTY ||
        worksheet.account?.workpaperCreditTY !== worksheet.account?.ledgerCreditTY;

    const unreconciledAmount = 'unreconciledDr' in worksheet ? worksheet.unreconciledDr ?? worksheet.unreconciledCr ?? null : null;

    function toggleVariance(): void {
        const value = params.get('varianceMode') === 'percent' ? 'amount' : 'percent';
        params.set('varianceMode', value);
        setParams(params);
    }

    return (
        <div className="grid grid-cols-[136px_auto] gap-x-8 gap-y-2 ">
            <Label>Status</Label>
            <StatusPreview worksheet={worksheet} />
            <Label>Assigned to</Label>
            {/* Assigned */}
            <Assigned worksheet={worksheet} size="mini" />

            {/* Account details */}
            {worksheet.account && (
                <>
                    {/* Type */}
                    <Label>Account Type</Label>
                    <label className="text-gray-800 text-sm">{worksheet.account.type}</label>
                    {/* Report code */}
                    <Label>Report Code</Label>
                    <label className="text-gray-800 text-sm">{worksheet.account.reportCode}</label>
                    {/* Current Amount */}
                    <Label>Current Amount</Label>
                    <div className="flex items-center gap-4">
                        <Currency
                            className="text-gray-800 text-sm"
                            as="label"
                            value={worksheet.account.workpaperDebitTY ?? worksheet.account.workpaperCreditTY}
                            withParentheses={Boolean(worksheet.account.workpaperCreditTY)}
                        />
                        {hasWorkpaperAmount && (
                            <div className="flex items-center">
                                <TooltipV2 message="Amount in ledger">
                                    <BanknotesIcon className="h-3.5 w-3.5 text-gray-600 mr-1" />
                                </TooltipV2>
                                <Currency
                                    className="text-gray-800 text-sm"
                                    as="label"
                                    value={worksheet.account.ledgerDebitLY ?? worksheet.account.ledgerCreditTY}
                                    withParentheses={Boolean(worksheet.account.ledgerCreditTY)}
                                />
                            </div>
                        )}
                    </div>
                    {/* Previous Amount */}
                    <Label>Comparative Amount</Label>
                    <div className="flex items-center gap-2">
                        <Currency
                            className="text-gray-800 text-sm"
                            as="label"
                            value={worksheet.account.ledgerDebitLY ?? worksheet.account.ledgerCreditLY}
                            withParentheses={Boolean(worksheet.account.ledgerCreditLY)}
                        />
                        {worksheet.account && <ClosingAmountDiffWarning worksheet={worksheet as WorksheetWithAccount} />}
                    </div>
                    <Label>Variance</Label>
                    <WorksheetVariance
                        worksheet={worksheet}
                        asPercent={params.get('varianceMode') === 'percent'}
                        onClick={toggleVariance}
                        textColor="text-gray-800"
                    />
                    {'unreconciledDr' in worksheet && (
                        <>
                            <Label>Unreconciled</Label>
                            <Currency
                                value={unreconciledAmount}
                                as="label"
                                withParentheses={typeof worksheet.unreconciledCr === 'number'}
                                className="text-gray-800 text-sm"
                            />
                        </>
                    )}
                    {worksheet.priorWorksheetId && workpaper.priorWorkpaperId && (
                        <Link
                            to={`/${shortId}/workpapers/${workpaper.priorWorkpaperId}/${
                                worksheet.account ? 'trial-balance' : 'other-schedules'
                            }/${worksheet.priorWorksheetId}`}
                            className="text-sm text-primary flex items-center gap-1 col-span-full mt-4"
                            target="_blank"
                        >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" /> Open prior worksheet
                        </Link>
                    )}
                </>
            )}
        </div>
    );
}

function Label({ children }: { children: string }): JSX.Element {
    return <label className="text-sm text-gray-600">{children}</label>;
}

export default PreviewDetails;
